import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { User, Shield, CreditCard, Download, Trash2, Copy, Gift, Loader2, Check, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [applyCode, setApplyCode] = useState("");
  const [consent, setConsent] = useState({ data_processing: true, analytics: false, marketing: false });
  const [connection, setConnection] = useState(null);
  const [stelleCode, setStelleCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [akteSharing, setAkteSharing] = useState(false);

  const fetchConnection = async () => {
    try { const res = await api.get("/participant/connection"); setConnection(res.data); } catch {}
  };

  const toggleAkteSharing = async () => {
    setAkteSharing(true);
    try {
      const endpoint = connection?.akte_shared ? "/participant/unshare-akte" : "/participant/share-akte";
      const res = await api.post(endpoint);
      toast.success(res.data.message);
      fetchConnection();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler bei der Akte-Freigabe"); }
    setAkteSharing(false);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [profRes, refRes] = await Promise.all([api.get("/profile"), api.get("/referral")]);
        setProfile(profRes.data);
        setName(profRes.data.user?.name || "");
        setReferralCode(refRes.data.code);
        if (profRes.data.consent) setConsent(profRes.data.consent);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
    fetchConnection();
  }, []);

  const updateName = async () => {
    try { await api.put("/profile", { name }); await refreshUser(); toast.success("Name aktualisiert"); } catch { toast.error("Name konnte nicht gespeichert werden"); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "mpu-coach-daten.json"; a.click();
      toast.success("Daten exportiert");
    } catch { toast.error("Export fehlgeschlagen"); }
    setExporting(false);
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try { await api.delete("/account/delete"); await logout(); navigate("/"); toast.success("Konto gelöscht"); } catch { toast.error("Fehler beim Löschen"); }
    setDeleting(false);
  };

  const saveConsent = async () => {
    try { await api.post("/account/consent", consent); toast.success("Einwilligungen gespeichert"); } catch { toast.error("Einwilligungen konnten nicht gespeichert werden"); }
  };

  const copyReferral = () => {
    const link = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Einladungslink kopiert!");
  };

  const applyReferralCode = async () => {
    if (!applyCode.trim()) return;
    try { const res = await api.post(`/referral/apply?code=${applyCode}`); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.detail || "Ungültiger Code"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="page-transition space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Mein Konto</h1>

      {/* Profile Info */}
      <Card className="border-border/50" data-testid="profile-info-card">
        <CardHeader><div className="flex items-center gap-2"><User className="w-5 h-5 text-primary" /><CardTitle className="font-heading text-lg">Profil</CardTitle></div></CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="font-body text-sm">E-Mail</Label><p className="font-body text-sm text-muted-foreground mt-1">{user?.email}</p></div>
          <div className="flex gap-3 items-end">
            <div className="flex-1"><Label className="font-body text-sm">Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 font-body" data-testid="profile-name-input" /></div>
            <Button onClick={updateName} className="font-body bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-name-btn">Speichern</Button>
          </div>
          <div><Label className="font-body text-sm">Abo-Status</Label><Badge variant="secondary" className="mt-1 font-body">{{"3monate":"3 Monate","6wochen":"6 Wochen","3wochen":"3 Wochen","free":"Kostenlos"}[profile?.subscription?.plan] || profile?.subscription?.plan_name || "Kostenlos"}</Badge></div>
        </CardContent>
      </Card>

      {/* Referral */}
      <Card className="border-border/50" data-testid="referral-card">
        <CardHeader><div className="flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /><CardTitle className="font-heading text-lg">Freunde einladen</CardTitle></div>
          <CardDescription className="font-body text-sm">Laden Sie Freunde ein und erhalten Sie beide je 3 Bonus-Sessions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={`${window.location.origin}/register?ref=${referralCode}`} readOnly className="font-body text-sm" data-testid="referral-link-input" />
            <Button variant="outline" onClick={copyReferral} className="gap-1 font-body" data-testid="copy-referral-btn"><Copy className="w-4 h-4" /> Kopieren</Button>
          </div>
          <Separator />
          <div className="flex gap-2">
            <Input value={applyCode} onChange={e => setApplyCode(e.target.value)} placeholder="Einladungscode eingeben" className="font-body" data-testid="apply-code-input" />
            <Button variant="outline" onClick={applyReferralCode} className="font-body" data-testid="apply-code-btn">Einlösen</Button>
          </div>
        </CardContent>
      </Card>

      {/* B2B Stelle Connection (DSGVO) */}
      <Card className="border-border/50" data-testid="stelle-connection-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            <CardTitle className="font-heading text-lg">Beratungsstelle</CardTitle>
          </div>
          <CardDescription className="font-body text-sm">Verbindung mit Ihrer MPU-Beratungsstelle</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {connection?.connected ? (
            <>
              <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                <p className="font-body text-sm font-medium">Verbunden mit: {connection.stelle_name}</p>
                <p className="font-body text-xs text-muted-foreground mt-0.5">Seit {connection.connected_since ? new Date(connection.connected_since).toLocaleDateString("de-DE") : "—"}</p>
              </div>
              <div>
                <p className="font-body text-xs font-medium mb-2">Was Ihre Beratungsstelle sehen kann:</p>
                <div className="space-y-1.5">
                  {[
                    { label: "Aktuelle Stufe im Lernpfad", shared: connection.shared?.stufe },
                    { label: "Kompetenzwerte (anonymisiert)", shared: connection.shared?.kompetenzwerte },
                    { label: "KI-Feedback zu Ihren Fortschritten", shared: connection.shared?.ki_feedback },
                    { label: "Ihre wörtlichen Antworten", shared: false },
                    { label: "Gesprächsverläufe", shared: false },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {item.shared ? <Check className="w-3.5 h-3.5 text-success" /> : <Unlink className="w-3.5 h-3.5 text-muted-foreground" />}
                      <span className={`font-body text-xs ${item.shared ? "" : "text-muted-foreground"}`}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* DSGVO: Akte-Freigabe Toggle */}
              <Separator />
              <div className="space-y-2" data-testid="akte-sharing-section">
                <p className="font-body text-xs font-medium">MPU-Akte Freigabe</p>
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/30">
                  <div>
                    <p className="font-body text-sm font-medium">Akte-Zusammenfassung teilen</p>
                    <p className="font-body text-[10px] text-muted-foreground">
                      {connection.akte_shared
                        ? "Ihre Beratungsstelle kann Delikt, BAK-Wert, Auflagen und Risiko einsehen."
                        : "Ihre Beratungsstelle hat keinen Zugriff auf Ihre Akte-Daten."}
                    </p>
                  </div>
                  <Switch
                    checked={connection.akte_shared || false}
                    onCheckedChange={toggleAkteSharing}
                    disabled={akteSharing}
                    data-testid="akte-sharing-toggle"
                  />
                </div>
                <p className="font-body text-[10px] text-muted-foreground">Sie können die Freigabe jederzeit widerrufen.</p>
              </div>
              <Button variant="outline" size="sm" className="font-body text-xs text-destructive hover:text-destructive gap-1"
                onClick={async () => {
                  setDisconnecting(true);
                  try { await api.post("/participant/disconnect"); toast.success("Verbindung getrennt"); fetchConnection(); } catch (e) { toast.error(e.response?.data?.detail || "Verbindung konnte nicht getrennt werden"); }
                  setDisconnecting(false);
                }} disabled={disconnecting} data-testid="disconnect-btn">
                {disconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
                Verbindung trennen
              </Button>
              <p className="font-body text-[10px] text-muted-foreground">Bei Trennung verliert Ihre Beratungsstelle sofort den Zugriff. Ihre Trainingsdaten bleiben erhalten.</p>
            </>
          ) : (
            <>
              <p className="font-body text-sm text-muted-foreground">Sie sind mit keiner Beratungsstelle verbunden.</p>
              <div className="flex gap-2">
                <Input value={stelleCode} onChange={e => setStelleCode(e.target.value.toUpperCase())} placeholder="Stellen-Code eingeben" className="font-body text-sm" data-testid="stelle-code-input" />
                <Button variant="outline" size="sm" className="font-body whitespace-nowrap"
                  onClick={async () => {
                    if (!stelleCode.trim()) return;
                    setLinking(true);
                    try { const res = await api.post("/expert/stelle-link", { stelle_code: stelleCode.trim() }); toast.success(res.data.message); fetchConnection(); setStelleCode(""); } catch (e) { toast.error(e.response?.data?.detail || "Code ungültig"); }
                    setLinking(false);
                  }} disabled={linking || !stelleCode.trim()} data-testid="link-stelle-btn">
                  {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verbinden"}
                </Button>
              </div>
              <p className="font-body text-[10px] text-muted-foreground">Fragen Sie Ihre Beratungsstelle nach dem Stellen-Code. Sie bestimmen, was geteilt wird.</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Privacy / Consent */}
      <Card className="border-border/50" data-testid="consent-card">
        <CardHeader><div className="flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /><CardTitle className="font-heading text-lg">Datenschutz & Einwilligungen</CardTitle></div></CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: "data_processing", label: "Datenverarbeitung", desc: "Erforderlich für die Nutzung des Dienstes", disabled: true },
            { key: "analytics", label: "Analyse-Cookies", desc: "Helfen uns, das Produkt zu verbessern" },
            { key: "marketing", label: "Marketing-Kommunikation", desc: "Gelegentliche Updates und Angebote" }
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between">
              <div><p className="font-body text-sm font-medium">{item.label}</p><p className="font-body text-xs text-muted-foreground">{item.desc}</p></div>
              <Switch checked={consent[item.key]} disabled={item.disabled} onCheckedChange={v => setConsent(c => ({ ...c, [item.key]: v }))} data-testid={`consent-${item.key}`} />
            </div>
          ))}
          <Button variant="outline" onClick={saveConsent} className="font-body" data-testid="save-consent-btn">Einwilligungen speichern</Button>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card className="border-border/50" data-testid="data-management-card">
        <CardHeader><CardTitle className="font-heading text-lg">Datenverwaltung</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={exportData} disabled={exporting} className="w-full font-body gap-2 justify-start" data-testid="export-data-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Alle meine Daten exportieren (JSON)
          </Button>
          <Button variant="outline" onClick={() => setShowDelete(true)} className="w-full font-body gap-2 justify-start text-error hover:text-error hover:bg-error/5" data-testid="delete-account-btn">
            <Trash2 className="w-4 h-4" /> Konto und alle Daten löschen
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={showDelete} onOpenChange={(open) => { setShowDelete(open); if (!open) setDeleteConfirmed(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Konto wirklich löschen?</DialogTitle>
            <DialogDescription className="font-body text-sm">
              Folgende Daten werden unwiderruflich gelöscht:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc pl-5">
              <li>Alle Trainingsgespräche und Analysen</li>
              <li>Ihr Lernfortschritt und Kompetenzen</li>
              <li>Verbindungen zu Beratungsstellen</li>
              <li>Hochgeladene Dokumente</li>
            </ul>
            <p className="font-body text-xs text-muted-foreground pt-2">Anonymisierte Zahlungsbelege werden gemäß steuerlicher Aufbewahrungspflichten (§ 147 AO) aufbewahrt.</p>
            <label className="flex items-start gap-2 pt-3 cursor-pointer">
              <input type="checkbox" checked={deleteConfirmed} onChange={(e) => setDeleteConfirmed(e.target.checked)} className="mt-0.5" data-testid="delete-confirm-checkbox" />
              <span className="font-body text-sm">Ich verstehe, dass alle meine Daten gelöscht werden und dieser Vorgang nicht rückgängig gemacht werden kann.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDelete(false); setDeleteConfirmed(false); }} className="font-body">Abbrechen</Button>
            <Button variant="destructive" onClick={deleteAccount} disabled={deleting || !deleteConfirmed} className="font-body gap-2" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Konto unwiderruflich löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
